<template>
  <div class="message-input-wrapper">
    <form @submit.prevent="handleSubmit" class="w-full">
      <div
        class="gradient-border-wrapper mx-4 mb-4"
        :class="{ 'show-glow': showGlow }"
      >
        <div
          class="message-input-container relative flex items-center gap-3 p-4 rounded-3xl transition-colors duration-200"
          :class="{ 'is-active': isFocused || isLoading }"
          @focusin="isFocused = true"
          @focusout="isFocused = false"
        >
          <Textarea
            ref="textareaRef"
            v-model="inputValue"
            :placeholder="placeholder"
            :disabled="isDisabled || isLoading"
            :maxlength="maxLength"
            class="flex-1 min-h-[20px] max-h-[120px] resize-none border-0 bg-transparent py-2 px-0 text-base text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-transparent hover:bg-transparent focus:bg-transparent"
            rows="1"
            @keydown="handleKeydown"
            :aria-label="inputAriaLabel"
          />

          <Button
            type="submit"
            :disabled="!canSend"
            variant="ghost"
            size="icon"
            class="h-10 w-10 rounded-full bg-muted/50 hover:bg-muted/70 text-muted-foreground shrink-0 transition-colors duration-200"
            :aria-label="sendButtonLabel"
            title="Send message"
          >
            <ArrowUp v-if="!isLoading" class="h-5 w-5" />

            <div v-else class="flex items-center justify-center">
              <div
                class="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
              ></div>
            </div>
          </Button>
        </div>
      </div>
    </form>

    <div
      v-if="showCharacterCount && maxLength"
      class="mx-4 mb-2 text-right text-xs text-muted-foreground"
      :class="{ 'text-destructive': isNearLimit }"
    >
      {{ inputValue.length }} / {{ maxLength }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'
import { Button, Textarea } from '@/lib/ui'
import { ArrowUp } from 'lucide-vue-next'
import { useChatSession } from '@/composables/useChatSession'
import { useChatbot } from '@/composables/useChatbot'
import type { IMessageInputProps } from './types'
import { logger } from '@/utils/logger'

const props = withDefaults(defineProps<IMessageInputProps>(), {
  placeholder: 'Ask Sidd anything...',
  maxLength: 2000,
  showCharacterCount: false,
  autoFocus: false,
})

const chatbot = useChatbot()
const chatSession = useChatSession({
  persistMessages: true,
  autoScrollToBottom: true,
})

const textareaRef = ref<InstanceType<typeof Textarea>>()
const inputValue = ref('')
const isFocused = ref(false)
const hasInteracted = ref(false)

const canSend = computed(() => {
  const trimmed = inputValue.value.trim()
  return (
    trimmed.length > 0 &&
    chatSession.canSendMessage &&
    (!props.maxLength || trimmed.length <= props.maxLength)
  )
})

const isLoading = computed(() => chatSession.store.isLoading)
const isDisabled = computed(
  () => !chatbot.store.configuration?.isActive || !chatSession.canSendMessage
)

const isNearLimit = computed(() => {
  if (!props.maxLength) return false
  return inputValue.value.length > props.maxLength * 0.8
})

const inputAriaLabel = computed(() => {
  if (isDisabled.value) return 'Message input disabled'
  if (isLoading.value) return 'Sending message...'
  return 'Type your message'
})

const sendButtonLabel = computed(() => {
  if (!canSend.value) return 'Send message (disabled)'
  if (isLoading.value) return 'Sending...'
  return 'Send message'
})

const showGlow = computed(() => {
  const hasMessages =
    chatSession.store.messages && chatSession.store.messages.length > 0
  if (
    hasMessages ||
    hasInteracted.value ||
    isFocused.value ||
    inputValue.value.length > 0 ||
    isLoading.value
  ) {
    return false
  }
  return true
})

const handleSubmit = async () => {
  const message = inputValue.value.trim()
  if (!canSend.value || !message) return

  inputValue.value = ''

  nextTick(() => {
    textareaRef.value?.$el?.focus()
  })

  try {
    if (!chatSession.store.activeSessionId) {
      await chatSession.startSession(chatbot.store.id!)
    }

    await chatSession.sendMessage(message)
  } catch (error) {
    logger.error(
      'Failed to send message',
      error,
      { component: 'MessageInput' },
      error instanceof Error ? error : undefined
    )
  }
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    handleSubmit()
    return
  }

  if (event.key === 'Enter' && event.shiftKey) {
    return
  }
}

watch(
  () => props.autoFocus,
  shouldFocus => {
    if (shouldFocus) {
      nextTick(() => {
        textareaRef.value?.$el?.focus()
      })
    }
  },
  { immediate: true }
)

watch(isFocused, focused => {
  if (focused && !hasInteracted.value) {
    hasInteracted.value = true
  }
})

defineExpose({
  focus: () => textareaRef.value?.$el?.focus(),
  clear: () => {
    inputValue.value = ''
  },
  setValue: (value: string) => {
    inputValue.value = value
    hasInteracted.value = true
  },
})
</script>

<style>
@property --gradient-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}

.gradient-border-wrapper {
  position: relative;
  padding: 2px;
  border-radius: 1.75rem;
  --gradient-angle: 0deg;
  background: rgba(0, 0, 0, 0.1);
}

.gradient-border-wrapper::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 1.75rem;
  background: conic-gradient(
    from var(--gradient-angle),
    transparent 0deg,
    rgba(107, 71, 224, 0.6) 60deg,
    rgba(160, 60, 200, 0.6) 110deg,
    rgba(220, 30, 120, 0.6) 150deg,
    rgba(71, 117, 224, 0.6) 210deg,
    rgba(90, 140, 230, 0.6) 260deg,
    rgba(107, 71, 224, 0.5) 330deg,
    transparent 360deg
  );
  opacity: 0;
  transition: opacity 0.6s ease-out;
  z-index: 0;
}

.gradient-border-wrapper::after {
  content: '';
  position: absolute;
  inset: -0.4rem;
  border-radius: 1.75rem;
  background: conic-gradient(
    from var(--gradient-angle),
    transparent 0deg,
    rgba(107, 71, 224, 0.7) 60deg,
    rgba(160, 60, 200, 0.7) 110deg,
    rgba(220, 30, 120, 0.7) 150deg,
    rgba(71, 117, 224, 0.7) 210deg,
    rgba(90, 140, 230, 0.7) 260deg,
    rgba(107, 71, 224, 0.6) 330deg,
    transparent 360deg
  );
  filter: blur(0.5rem);
  opacity: 0;
  transition: opacity 0.6s ease-out;
  z-index: -1;
}

.gradient-border-wrapper.show-glow::before {
  opacity: 1;
  animation: rotate-gradient 4s linear infinite;
}

.gradient-border-wrapper.show-glow::after {
  opacity: 0.15;
  animation: rotate-gradient 4s linear infinite;
}

@keyframes rotate-gradient {
  100% {
    --gradient-angle: 360deg;
  }
}

.message-input-container {
  position: relative;
  background: white;
  border-radius: calc(1.75rem - 3px);
  z-index: 1;
}

/* Force disable textarea resize handle - important for embedded usage */
.message-input-container textarea {
  resize: none !important;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}
</style>
